import React, { useEffect, useMemo, useState } from "react";
import FilterSidebar, {
  FilterSidebarProps,
  SortValue,
} from "../FilterSidebar/FilterSidebar";
import ShirtsGrid from "./ShirtsGrid";

type GridProps = any;

const DEFAULT_SORT_OPTIONS = [
  { value: "relevance" as SortValue, label: "Relevantie" },
  { value: "price-asc" as SortValue, label: "Prijs oplopend" },
  { value: "price-desc" as SortValue, label: "Prijs aflopend" },
  { value: "newest" as SortValue, label: "Nieuwste eerst" },
  { value: "season-desc" as SortValue, label: "Seizoen ↓" },
  { value: "season-asc" as SortValue, label: "Seizoen ↑" },
  { value: "views-desc" as SortValue, label: "Meest bekeken" },
];

interface CatalogSectionProps {
  filterProps: FilterSidebarProps;
  gridPropsTop?: GridProps;
  gridPropsMain: GridProps;
}

export default function CatalogSection({
  filterProps,
  gridPropsTop,
  gridPropsMain,
}: CatalogSectionProps) {
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (filterOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [filterOpen]);

  const mergedFilterProps = useMemo<FilterSidebarProps>(
    () => ({
      ...filterProps,
      onApply: () => {
        filterProps.onApply?.();
        setFilterOpen(false);
      },
    }),
    [filterProps],
  );

  const sortOptions = filterProps.sortOptions ?? DEFAULT_SORT_OPTIONS;
  const sortBy = filterProps.sortBy ?? "relevance";
  const onSortBy = filterProps.onSortBy ?? (() => {});

  return (
    <section className="catalog catalog-section">
      <div className="catalog-section__inner">
        <div className="catalog-section__topbar">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filterOpen}
            className="catalog-section__filter-trigger"
          >
            <span aria-hidden="true" className="catalog-section__filter-icon">
              ☰
            </span>
            Filters
          </button>

          <select
            className="catalog-section__sort-select"
            value={sortBy}
            onChange={(e) => onSortBy(e.target.value as SortValue)}
            aria-label="Sorteren op"
          >
            {sortOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <div className="catalog-section__results" aria-live="polite">
            {filterProps.resultsCount ?? 0} resultaten
          </div>
        </div>

        <div>
          {gridPropsTop && (
            <div className="catalog-section__top-grid">
              <ShirtsGrid {...gridPropsTop} />
            </div>
          )}

          <ShirtsGrid {...gridPropsMain} />
        </div>
      </div>

      {filterOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
          onClick={() => setFilterOpen(false)}
          className="catalog-filter-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="catalog-filter-drawer"
          >
            <div className="catalog-filter-drawer__head">
              <div className="catalog-filter-drawer__title">Filters</div>

              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="Sluit filters"
                className="catalog-filter-drawer__close"
              >
                ×
              </button>
            </div>

            <div className="catalog-filter-drawer__body">
              <FilterSidebar {...mergedFilterProps} />
            </div>
          </div>

          <style>
            {`
              @keyframes catalogDrawerIn {
                from {
                  opacity: 0;
                  transform: translateX(-18px);
                }
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
            `}
          </style>
        </div>
      )}
    </section>
  );
}
